import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Flex,
  Box,
  Text,
  Select,
  Divider,
  Popover,
  Center,
  Image,
  UnstyledButton,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCalendar,
  IconCircleCheck,
  IconLink,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useStyles } from "./style";
import { useSelector } from "react-redux";
import { searchStaffSkypoint } from "../../services/staff";
import {
  addSkypointProjectTask,
  updateSkypointProjectTask,
} from "../../services/skypointProject";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import moment from "moment";
import RemoteSearchSelect from "../RemoteSearchSelect";

const UNASSIGNED = { value: "unassigned", label: "Unassigned" };

const NewTaskModal = ({ opened, onClose, mode, selectedTask }) => {
  const { classes } = useStyles();
  const editorRef = useRef(null);
  const isViewMode = mode === "view";
  const { skypointProjectStatus } = useSelector(
    (state) => state?.skypointProject,
  );
  const todoId = "6943cd7d61843a744d1e4868";
  const doneStatus = (skypointProjectStatus || []).find(
    (s) => s?.status?.toLowerCase() === "done",
  );

  const { profile } = useSelector((state) => state?.profile);

  const [formData, setFormData] = useState({
    status: todoId,
    type: "",
    title: "",
    date: null,
    assignedTo: "unassigned",
    description: "",
  });
  // selectedImageUrl for preview, selectedFile for uploading
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [urlPopoverOpened, setUrlPopoverOpened] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [attachedLink, setAttachedLink] = useState("");
  const [imgPreviewOpened, setImgPreviewOpened] = useState(false);
  const [editorInitialized, setEditorInitialized] = useState(false);
  // validation errors object
  const [errors, setErrors] = useState({});

  // loading state for submit
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);
  const [savedSelection, setSavedSelection] = useState(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setSavedSelection(sel.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (savedSelection) sel.addRange(savedSelection);
  };

  const handleAddLink = () => {
    // const selection = window.getSelection();
    // if (!selection || selection.rangeCount === 0) return;
    if (!savedSelection) return;

    restoreSelection();

    // const range = selection.getRangeAt(0);
    const range = window.getSelection().getRangeAt(0);

    const link = document.createElement("a");
    let href = urlValue.trim();
    if (!/^https?:\/\//i.test(href)) {
      href = "https://" + href;
    }
    link.href = href;
    link.target = "_blank";
    link.style.cursor = "pointer";
    link.textContent = range.toString();

    range.deleteContents();
    range.insertNode(link);

    // selection.removeAllRanges();
    setSavedSelection(null);
    setUrlValue("");
    setUrlPopoverOpened(false);

    // Update form state with new HTML
    if (editorRef.current) {
      setFormData((p) => ({
        ...p,
        description: editorRef.current.innerHTML,
      }));
    }
  };

  // Staff are searched on the server as you type: the whole list stopped at the first rows
  // (OD-1694), and every board card loaded it again
  const staffOption = (s) => ({
    value: s?.id,
    label: `${s?.name}${s?.id === profile?.user?.id ? " (Admin)" : ""}`,
  });
  const fetchStaffOptions = async (search) => {
    const { staffs, count } = await searchStaffSkypoint(search);
    return { options: staffs.map(staffOption), total: count };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      if (mode === "add") {
        const payload = {
          ...formData,
          assignedTo:
            formData?.assignedTo === "unassigned" ? null : formData?.assignedTo,
          link: attachedLink || null,
          description: editorRef.current.innerHTML,
        };
        if (selectedFile) {
          const result = await uploadFileToS3Api({ file: selectedFile });
          payload.image = result?.uploadURL || null;
        }
        console.log(
          "🚀 ~ handleSubmit ~ payload:",
          payload,
          editorRef.current.innerHTML,
        );
        console.log("HTML:", editorRef.current.innerHTML);
        const res = await addSkypointProjectTask(payload);
        res && handleCancel();
      } else if (mode === "edit") {
        const payload = {
          ...formData,
          assignedTo:
            formData?.assignedTo === "unassigned" ? null : formData?.assignedTo,
          id: selectedTask?.id,
          link: attachedLink || null,
          description: editorRef.current.innerHTML,
        };
        console.log(
          "🚀 ~ handleSubmit ~ payload:",
          payload,
          editorRef.current.innerHTML,
        );
        if (selectedFile) {
          const result = await uploadFileToS3Api({ file: selectedFile });
          payload.image = result?.uploadURL || null;
        }
        const res = await updateSkypointProjectTask(payload);
        res && handleCancel();
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsDone = async () => {
    if (!doneStatus?.id || !selectedTask?.id) return;
    setLoading(true);
    try {
      await updateSkypointProjectTask({
        id: selectedTask.id,
        status: doneStatus.id,
      });
      handleCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      status: todoId,
      type: "",
      title: "",
      date: null,
      assignedTo: "unassigned",
      description: "",
    });

    setSelectedImage(null);
    setSelectedFile(null);
    setAttachedLink("");
    setUrlValue("");
    setErrors({});
    setEditorInitialized(false);
    if (fileInputRef.current) fileInputRef.current.value = "";

    onClose();
  };

  const handleImageClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageURL = URL.createObjectURL(file);

    // store both preview URL and the actual File for upload
    setSelectedImage(imageURL);
    setSelectedFile(file);

    // reset input value so same file can be selected later if needed
    // but we already stored the file in state
    e.target.value = null;
  };

  const validateForm = () => {
    const errs = {};
    // required: status, title, date, description, type
    if (!formData.status || formData.status.toString().trim() === "") {
      errs.status = "Status is required";
    }

    if (!formData.title || formData.title.toString().trim() === "") {
      errs.title = "Title is required";
    }

    // if (!formData.date) {
    //   errs.date = "Date is required";
    // }

    if (
      !formData.description ||
      formData.description.toString().trim() === ""
    ) {
      errs.description = "Description is required";
    }

    if (!formData.type || formData.type.toString().trim() === "") {
      errs.type = "Type is required";
    }

    return { valid: Object.keys(errs).length === 0, errors: errs };
  };

  const handleAddUrl = () => {
    if (!urlValue.trim()) return;

    setAttachedLink(urlValue.trim());

    setUrlValue("");
    setUrlPopoverOpened(false);
  };

  useEffect(() => {
    if (selectedTask) {
      setFormData({
        status: selectedTask?.status?.id || todoId,
        type: selectedTask?.type || "",
        title: selectedTask?.title || "",
        date: selectedTask?.date ? moment(selectedTask.date).toDate() : null,
        assignedTo: selectedTask?.assignedTo?.id || "unassigned",
        description: selectedTask?.description || "",
      });
      setSelectedImage(selectedTask?.image || null);
      setAttachedLink(selectedTask?.link || "");
    }
  }, [selectedTask]);

  const handleInput = (e) => {
    if (!e.currentTarget) return; // safety check
    const html = e.currentTarget.innerHTML;
    setFormData({ ...formData, description: html });
    console.log("🚀 ~ handleInput ~ FormData:", formData);

    setErrors((p) => ({ ...p, description: undefined }));
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      onTransitionEnd={() => {
        if (!editorInitialized && editorRef.current && selectedTask) {
          editorRef.current.innerHTML = selectedTask.description || "";
          setFormData((p) => ({
            ...p,
            description: selectedTask.description || "",
          }));
          setEditorInitialized(true); // mark as initialized
        }
      }}
      title={
        <Text size="lg" weight={700} style={{ fontFamily: "Inter" }}>
          {isViewMode
            ? "View Task"
            : mode === "edit"
              ? "Edit Task"
              : "New Task"}
        </Text>
      }
      size="xl"
      centered
      zIndex={1000}
      padding={0}
      styles={{
        root: classes.root,
        inner: classes.inner,
        modal: classes.modal,
        header: classes.header,
        body: classes.body,
        close: classes.close,
      }}
      closeButtonProps={{
        icon: <IconX size={20} />,
      }}
      overlayProps={{ opacity: 0.5, blur: 4 }}
      overlayOpacity={0.5}
    >
      <Box p="xl" className={classes.mainContent}>
        <Box className={classes.formContainer}>
          <Flex gap="md" mb="md" direction={{ base: "column", sm: "row" }}>
            <Box style={{ flex: 1 }}>
              <Text
                size="14px"
                weight={500}
                style={{ fontFamily: "Inter" }}
                color="#262626"
              >
                Ticket Type
              </Text>
              <Select
                placeholder="Select"
                data={[{ value: "task", label: "Task" }]}
                value={formData.type}
                onChange={(value) => {
                  setFormData({ ...formData, type: value });
                  setErrors((p) => ({ ...p, type: undefined }));
                }}
                searchable
                disabled={isViewMode}
              />
              {errors.type && (
                <Text size="xs" color="red" mt={6}>
                  {errors.type}
                </Text>
              )}
            </Box>
            <Box style={{ flex: 1 }}>
              <Text
                size="14px"
                weight={500}
                style={{ fontFamily: "Inter" }}
                color="#262626"
              >
                Status
              </Text>
              <Select
                style={{ textTransform: "capitalize" }}
                placeholder="Select status"
                data={(skypointProjectStatus || []).map(({ id, status }) => ({
                  value: id,
                  label: status,
                }))}
                value={formData.status}
                onChange={(value) => {
                  setFormData({ ...formData, status: value });
                  setErrors((p) => ({ ...p, status: undefined }));
                }}
                searchable
                disabled={isViewMode}
              />
              {errors.status && (
                <Text size="xs" color="red" mt={6}>
                  {errors.status}
                </Text>
              )}
            </Box>
          </Flex>

          <Box mb="md">
            <Text size="sm" weight={500} style={{ fontFamily: "Inter" }}>
              Title
            </Text>
            <TextInput
              placeholder="Home of our loving abode"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setErrors((p) => ({ ...p, title: undefined }));
              }}
              disabled={isViewMode}
            />
            {errors.title && (
              <Text size="xs" color="red" mt={6}>
                {errors.title}
              </Text>
            )}
          </Box>

          <Divider mb="md" mt="xl" color="#e9ecef" />

          <Box>
            <Text size="sm" weight={500} mb={8} style={{ fontFamily: "Inter" }}>
              Description
            </Text>
            <Box
              style={{
                border: "1px solid #e9ecef",
                borderRadius: "12px",
                backgroundColor: "#f9fafb",
                overflow: "hidden",
              }}
            >
              {!isViewMode && (
                <Flex
                  gap="md"
                  px="md"
                  py="sm"
                  justify="center"
                  style={{
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <Popover
                    opened={urlPopoverOpened}
                    onChange={setUrlPopoverOpened}
                    onMouseDown={saveSelection}
                    position="bottom-start"
                    shadow="md"
                  >
                    <Popover.Target>
                      <Button
                        variant="subtle"
                        leftIcon={<IconLink size={14} />}
                        size="xs"
                        compact
                        onClick={() => setUrlPopoverOpened((o) => !o)}
                        styles={{
                          root: classes.subtleButtonRoot,
                          leftIcon: classes.subtleButtonLeftIcon,
                        }}
                      >
                        URL label
                      </Button>
                    </Popover.Target>

                    <Popover.Dropdown style={{ width: 260 }}>
                      <Text size="sm" weight={500} mb={8}>
                        Attach Link
                      </Text>

                      <TextInput
                        placeholder="https://example.com"
                        value={urlValue}
                        onChange={(e) => setUrlValue(e.target.value)}
                        mb={12}
                      />

                      <Flex justify="flex-end" gap={8}>
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => setUrlPopoverOpened(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="xs"
                          variant="white"
                          c="white"
                          // onClick={handleAddUrl}
                          onClick={() => {
                            handleAddLink();
                          }}
                          bg="yellow"
                        >
                          Attach
                        </Button>
                      </Flex>
                    </Popover.Dropdown>
                  </Popover>

                  <Button
                    variant="subtle"
                    leftIcon={<IconUpload size={14} />}
                    size="xs"
                    compact
                    onClick={handleImageClick}
                    styles={{
                      root: classes.subtleButtonRoot,
                      leftIcon: classes.subtleButtonLeftIcon,
                    }}
                  >
                    Upload IMG
                  </Button>
                </Flex>
              )}

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageSelected}
                style={{ display: "none" }}
              />

              <div
                contentEditable={!isViewMode}
                ref={editorRef}
                minRows={4}
                className={classes.textareaInput}
                suppressContentEditableWarning
                // onInput={(e) => {
                //   setFormData({ ...formData, description: e.currentTarget.innerHTML });
                //   setErrors((p) => ({ ...p, description: undefined }))
                // }}
                onInput={handleInput}
                onClick={(e) => {
                  if (
                    e.target.tagName === "A" &&
                    e.target.getAttribute("href") &&
                    !isViewMode
                  ) {
                    let href = e.target.getAttribute("href").trim();
                    if (!/^https?:\/\//i.test(href)) {
                      href = "https://" + href;
                    }
                    window.open(href, "_blank");
                  }
                }}
                data-placeholder="Type something here..."
              />

              {errors.description && (
                <Text size="xs" color="red" mt={6} style={{ paddingLeft: 12 }}>
                  {errors.description}
                </Text>
              )}

              {selectedImage && (
                <Flex direction="column" w="fit-content">
                  <Box
                    style={{
                      position: "relative",
                      width: "80px",
                      height: "80px",
                      margin: "10px",
                    }}
                  >
                    {!isViewMode && (
                      <Button
                        onClick={() => {
                          setSelectedImage(null);
                          setSelectedFile(null);
                          if (fileInputRef.current)
                            fileInputRef.current.value = null;
                        }}
                        variant="white"
                        size="xs"
                        className={classes.deleteImageButton}
                        style={{ position: "absolute", right: -6, top: -6 }}
                      >
                        ✕
                      </Button>
                    )}

                    <img
                      src={selectedImage}
                      onClick={() => setImgPreviewOpened(true)}
                      alt="Uploaded Preview"
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                  </Box>
                </Flex>
              )}

              {attachedLink && (
                <Flex gap={8} wrap="wrap" px="md" pb="sm" mt={10}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      background: "#ffffff",
                      border: "1px solid #e9ecef",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    <IconLink size={14} />
                    <a
                      href={attachedLink}
                      target="_blank"
                      style={{ color: "#000", textDecoration: "none" }}
                    >
                      {attachedLink}
                    </a>

                    {!isViewMode && (
                      <IconX
                        size={12}
                        style={{ cursor: "pointer" }}
                        onClick={() => setAttachedLink("")}
                      />
                    )}
                  </Box>
                </Flex>
              )}
            </Box>
          </Box>

          <Flex
            gap="md"
            mb="md"
            mt="md"
            direction={{ base: "column", sm: "row" }}
          >
            <Box style={{ flex: 1 }}>
              <Text size="sm" weight={500} style={{ fontFamily: "Inter" }}>
                Due Date
              </Text>
              <DateInput
                placeholder="Select date"
                icon={<IconCalendar size={16} color="#262626" />}
                value={formData.date}
                onChange={(value) => {
                  const valid = value instanceof Date && !isNaN(value);
                  setFormData({ ...formData, date: valid ? value : null });
                  setErrors((p) => ({ ...p, date: undefined }));
                }}
                valueFormat="MMM DD, YYYY"
                disabled={isViewMode}
              />
              {errors.date && (
                <Text size="xs" color="red" mt={6}>
                  {errors.date}
                </Text>
              )}
            </Box>

            <Box style={{ flex: 1 }}>
              <Text size="sm" weight={500} style={{ fontFamily: "Inter" }}>
                Assign to:
              </Text>
              <RemoteSearchSelect
                placeholder="Select staff member"
                fetchOptions={fetchStaffOptions}
                fixedOptions={[UNASSIGNED]}
                selectedOption={
                  selectedTask?.assignedTo
                    ? staffOption(selectedTask.assignedTo)
                    : null
                }
                noun="staff"
                nothingFound="No active staff"
                value={formData.assignedTo}
                onChange={(value) =>
                  setFormData({ ...formData, assignedTo: value })
                }
                disabled={isViewMode}
              />
            </Box>
          </Flex>

          {(() => {
            const isAlreadyDone =
              selectedTask?.status?.status?.toLowerCase() === "done";
            const canMarkDone =
              !!selectedTask?.id && !!doneStatus?.id && !isAlreadyDone;

            if (isViewMode) {
              if (!canMarkDone) return null;
              return (
                <Flex gap="md" mt="xl" justify="flex-end">
                  <Button
                    size="md"
                    color="green"
                    leftIcon={<IconCircleCheck size={16} />}
                    onClick={handleMarkAsDone}
                    loading={loading}
                    disabled={loading}
                  >
                    Mark as done
                  </Button>
                </Flex>
              );
            }

            return (
              <Flex
                gap="md"
                mt="xl"
                direction={{ base: "column", xs: "row" }}
                align={{ base: "stretch", xs: "center" }}
              >
                {mode !== "edit" && canMarkDone && (
                  <Button
                    size="md"
                    color="green"
                    variant="light"
                    leftIcon={<IconCircleCheck size={16} />}
                    onClick={handleMarkAsDone}
                    disabled={loading}
                  >
                    Mark as done
                  </Button>
                )}
                <Button
                  variant="default"
                  fullWidth
                  size="md"
                  onClick={handleCancel}
                  className={classes.cancelButton}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  size="md"
                  onClick={handleSubmit}
                  className={classes.createButton}
                  loading={loading}
                  disabled={loading}
                >
                  {mode === "edit" ? "Update" : "Save"}
                </Button>
              </Flex>
            );
          })()}
        </Box>
      </Box>
      <Modal
        opened={imgPreviewOpened}
        onClose={() => setImgPreviewOpened(false)}
        centered
        size="lg"
        zIndex={100000000}
        withCloseButton
        overlayOpacity={0.55}
      >
        <Center>
          <Image
            src={selectedImage}
            alt=""
            fit="contain"
            style={{ maxHeight: "80vh" }}
          />
        </Center>
      </Modal>
    </Modal>
  );
};

export default NewTaskModal;
